import { indentWithTab } from '@codemirror/commands';
import { cpp } from '@codemirror/lang-cpp';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { setDiagnostics } from '@codemirror/lint';
import { Compartment, EditorState } from '@codemirror/state';
import { Decoration, EditorView, GutterMarker, gutter, keymap } from '@codemirror/view';
import { tags } from '@lezer/highlight';
import { basicSetup } from 'codemirror';
import { useEffect, useRef } from 'react';
import type { DebugVariable } from '../lib/debug/types';
import type { SourceDiagnostic } from '../lib/diagnostics';
import type { Theme } from '../lib/theme';

interface Props {
	theme: Theme;
	source: string;
	onChange: (source: string) => void;
	diagnostics: SourceDiagnostic[];
	readOnly: boolean;
	currentLine?: number;
	breakpoints: number[];
	onToggleBreakpoint: (line: number) => void;
	debugVariables: DebugVariable[];
	highlightedVariableKey: string | null;
	onHoverVariable: (key: string | null) => void;
}

class BreakpointMarker extends GutterMarker {
	constructor(
		readonly line: number,
		readonly active: boolean,
		readonly toggle: (line: number) => void,
	) {
		super();
	}
	eq(other: BreakpointMarker) {
		return this.line === other.line && this.active === other.active;
	}
	toDOM() {
		const button = document.createElement('button');
		button.className = `breakpoint-marker ${this.active ? 'active' : ''}`;
		button.setAttribute('aria-label', `Toggle breakpoint on line ${this.line}`);
		button.setAttribute('aria-pressed', String(this.active));
		button.title = `Breakpoint · line ${this.line}`;
		button.textContent = '●';
		button.onmousedown = (event) => event.preventDefault();
		button.onclick = () => this.toggle(this.line);
		return button;
	}
}

export function CodeEditor({
	theme,
	source,
	onChange,
	diagnostics,
	readOnly,
	currentLine,
	breakpoints,
	onToggleBreakpoint,
	debugVariables,
	highlightedVariableKey,
	onHoverVariable,
}: Props) {
	const host = useRef<HTMLDivElement>(null);
	const view = useRef<EditorView | null>(null);
	const change = useRef(onChange);
	const editable = useRef(new Compartment());
	const colorScheme = useRef(new Compartment());
	const debugGutter = useRef(new Compartment());
	const execution = useRef(new Compartment());
	const variableLinks = useRef(new Compartment());
	const hover = useRef(onHoverVariable);
	hover.current = onHoverVariable;
	const toggle = useRef(onToggleBreakpoint);
	toggle.current = onToggleBreakpoint;
	change.current = onChange;

	// biome-ignore lint/correctness/useExhaustiveDependencies: the editor is created once; the effects below synchronize source, readOnly, and decorations
	useEffect(() => {
		if (!host.current) return;
		const editor = new EditorView({
			parent: host.current,
			state: EditorState.create({
				doc: source,
				extensions: [
					debugGutter.current.of([]),
					colorScheme.current.of(EditorView.theme({}, { dark: theme === 'dark' })),
					basicSetup,
					keymap.of([indentWithTab]),
					cpp(),
					editable.current.of(EditorState.readOnly.of(readOnly)),
					execution.current.of([]),
					variableLinks.current.of([]),
					EditorView.domEventHandlers({
						mousemove: (event) => {
							const token =
								event.target instanceof Element
									? event.target.closest<HTMLElement>('[data-debug-variable-key]')
									: null;
							hover.current(token?.dataset.debugVariableKey ?? null);
						},
						mouseleave: () => hover.current(null),
					}),
					syntaxHighlighting(
						HighlightStyle.define([
							{ tag: tags.keyword, color: 'var(--syntax-keyword)' },
							{
								tag: [tags.typeName, tags.className],
								color: 'var(--syntax-type)',
							},
							{
								tag: [tags.string, tags.character],
								color: 'var(--syntax-string)',
							},
							{
								tag: [tags.number, tags.bool, tags.null],
								color: 'var(--syntax-number)',
							},
							{ tag: tags.comment, color: 'var(--syntax-comment)' },
							{
								tag: [tags.meta, tags.processingInstruction],
								color: 'var(--syntax-meta)',
							},
							{
								tag: tags.function(tags.variableName),
								color: 'var(--syntax-function)',
							},
							{
								tag: [tags.operator, tags.punctuation],
								color: 'var(--syntax-punctuation)',
							},
						]),
					),
					EditorView.contentAttributes.of({
						'aria-label': 'C source',
						spellcheck: 'false',
					}),
					EditorView.updateListener.of((update) => {
						if (update.docChanged) change.current(update.state.doc.toString());
					}),
					EditorView.theme({
						'&': {
							height: '100%',
							fontSize: '14px',
							backgroundColor: 'var(--editor)',
							color: 'var(--code-text)',
						},
						'&.cm-focused': { outline: 'none' },
						'.cm-scroller': {
							overflow: 'auto',
							fontFamily: 'var(--mono)',
							lineHeight: '1.65',
							scrollbarWidth: 'thin',
						},
						'.cm-content': { padding: '5px 0', caretColor: 'var(--text)' },
						'.cm-line': { padding: '0 12px' },
						'.cm-gutters': {
							backgroundColor: 'var(--editor)',
							border: 'none',
							color: 'var(--gutter-text)',
							paddingRight: '8px',
						},
						'.cm-lineNumbers .cm-gutterElement': {
							padding: '0 8px 0 16px',
							minWidth: '40px',
						},
						'.cm-activeLine, .cm-activeLineGutter': {
							backgroundColor: 'var(--surface)',
						},
						'.cm-activeLineGutter': { color: 'var(--gutter-active)' },
						'.cm-selectionBackground, &.cm-focused .cm-selectionBackground': {
							backgroundColor: 'var(--selection)',
						},
						'.cm-cursor': { borderLeftColor: 'var(--text)' },
						'.cm-tooltip': {
							backgroundColor: 'var(--editor)',
							border: '1px solid var(--border)',
							color: 'var(--text)',
						},
						'.cm-panels': {
							backgroundColor: 'var(--panel)',
							color: 'var(--text)',
						},
						'.cm-searchMatch': { backgroundColor: 'var(--search-match)' },
						'.cm-foldPlaceholder': {
							backgroundColor: 'var(--panel)',
							border: 'none',
							color: 'var(--muted)',
						},
						'.cm-execution-line': {
							backgroundColor: 'var(--execution-background) !important',
							boxShadow: 'inset 3px 0 var(--execution-accent)',
						},
						'.cm-breakpoints .cm-gutterElement': {
							padding: '0',
							display: 'flex',
							alignItems: 'center',
						},
					}),
				],
			}),
		});
		view.current = editor;
		return () => {
			editor.destroy();
			view.current = null;
		};
		// The editor is created once; the effects below synchronize external state.
	}, []);

	useEffect(() => {
		view.current?.dispatch({
			effects: colorScheme.current.reconfigure(EditorView.theme({}, { dark: theme === 'dark' })),
		});
	}, [theme]);

	useEffect(() => {
		const editor = view.current;
		if (editor && source !== editor.state.doc.toString()) {
			editor.dispatch({
				changes: { from: 0, to: editor.state.doc.length, insert: source },
			});
		}
	}, [source]);

	useEffect(() => {
		view.current?.dispatch({
			effects: editable.current.reconfigure(EditorState.readOnly.of(readOnly)),
		});
	}, [readOnly]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: source is intentionally tracked to re-map diagnostic positions when the document changes
	useEffect(() => {
		const editor = view.current;
		if (!editor) return;
		editor.dispatch(
			setDiagnostics(
				editor.state,
				diagnostics
					.filter((d) => d.line > 0 && d.line <= editor.state.doc.lines)
					.map((d) => {
						const line = editor.state.doc.line(d.line);
						// Clang reports UTF-8 byte columns; CodeMirror positions use UTF-16 code units.
						const prefix = new TextEncoder().encode(line.text).slice(0, Math.max(0, d.column - 1));
						const from = Math.min(line.to, line.from + new TextDecoder().decode(prefix).length);
						return {
							from,
							to: Math.min(line.to, from + 1),
							severity: d.severity,
							message: d.message,
						};
					}),
			),
		);
	}, [diagnostics, source]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: source is intentionally tracked to re-render gutter markers when the document changes
	useEffect(() => {
		const editor = view.current;
		if (!editor) return;
		editor.dispatch({
			effects: debugGutter.current.reconfigure(
				gutter({
					class: 'cm-breakpoints',
					lineMarker: (view, line) => {
						const number = view.state.doc.lineAt(line.from).number;
						return new BreakpointMarker(number, breakpoints.includes(number), (line) =>
							toggle.current(line),
						);
					},
				}),
			),
		});
		// CodeMirror hides decorative gutters from accessibility by default. This
		// gutter contains real buttons, so expose it while keeping line numbers hidden.
		editor.dom.querySelector('.cm-gutters')?.removeAttribute('aria-hidden');
		editor.dom.querySelectorAll('.cm-gutter:not(.cm-breakpoints)').forEach((element) => {
			element.setAttribute('aria-hidden', 'true');
		});
	}, [breakpoints, source]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: source is intentionally tracked to re-apply the execution-line decoration when the document changes
	useEffect(() => {
		const editor = view.current;
		if (!editor) return;
		const line =
			currentLine && currentLine <= editor.state.doc.lines
				? editor.state.doc.line(currentLine)
				: undefined;
		const decorations = line
			? Decoration.set([
					Decoration.line({
						attributes: {
							class: 'cm-execution-line',
							'data-current-line': String(currentLine),
						},
					}).range(line.from),
				])
			: Decoration.none;
		editor.dispatch({
			effects: [
				execution.current.reconfigure(EditorView.decorations.of(decorations)),
				...(line ? [EditorView.scrollIntoView(line.from, { y: 'nearest' })] : []),
			],
		});
	}, [currentLine, source]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: source is intentionally tracked to re-clamp variable ranges when the document changes
	useEffect(() => {
		const editor = view.current;
		if (!editor) return;
		const ranges = new Map<number, { to: number; key: string }>();
		for (const variable of debugVariables) {
			for (const range of variable.sourceRanges) {
				if (
					range.from >= 0 &&
					range.to <= editor.state.doc.length &&
					range.to > range.from &&
					!ranges.has(range.from)
				)
					ranges.set(range.from, { to: range.to, key: variable.key });
			}
		}
		const marks = [...ranges].map(([from, { to, key }]) =>
			Decoration.mark({
				class: `cm-debug-variable${key === highlightedVariableKey ? ' cm-variable-linked' : ''}`,
				attributes: { 'data-debug-variable-key': key },
			}).range(from, to),
		);
		editor.dispatch({
			effects: variableLinks.current.reconfigure(
				EditorView.decorations.of(Decoration.set(marks, true)),
			),
		});
	}, [debugVariables, highlightedVariableKey, source]);

	return <div className="editor-host" ref={host} />;
}
